import { InfoRefElement } from "@/atv/eInvoiceApi";
import { InvoiceDataType } from "@/api/InvoiceApi";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import momentTz from "moment-timezone";
import {
    REFERENCES_CODES,
    REFERENCES_INFORMATION,
    ReferencedDocumentClass,
    resolveDefaultTipoDocIR,
    resolveReferencedDocumentType,
} from "./Constants";
import { isEDocumentAtvAccepted, parseEDocumentStatusPayload } from "./edocumentAtvStatus";

const computeRfc3339 = <T>(data: T, field: keyof T): string => {
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || momentTz.tz.guess() || "UTC";
    const raw = data?.[field] as unknown;
    let m = momentTz.tz(userTz);
    if (raw != null) {
        if (raw instanceof Date) {
            m = momentTz(raw).tz(userTz);
        } else if (typeof raw === "number") {
            m = momentTz(raw).tz(userTz);
        } else if (typeof raw === "string") {
            const looksISOWithZone = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) || raw.includes("T");
            if (looksISOWithZone) {
                m = momentTz.parseZone(raw).tz(userTz);
            } else {
                m = momentTz.tz(raw, "YYYY-MM-DD HH:mm:ss", userTz);
            }
        }
    }
    if (!m.isValid()) {
        m = momentTz.tz(userTz);
    }
    return m.format();
};

export type ReferenceSource = "auto" | "manual_internal" | "manual_external";

export type ReferenceLinkedDocumentKind =
    | "Invoice"
    | "Creditnote"
    | "Debitnote"
    | "BuyInvoice";

export type StoredReference = InfoRefElement & {
    id: string;
    source: ReferenceSource;
    linkedDocumentId?: string;
    linkedDocumentKind?: ReferenceLinkedDocumentKind;
};

export const MAX_DOCUMENT_REFERENCES = 10;
export const REFERENCE_NUMBER_MAX_LENGTH = 50;
export const REFERENCE_REASON_MAX_LENGTH = 180;

const generateLocalId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `ref_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const isInfoRefShape = (value: unknown): value is InfoRefElement => {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Record<string, unknown>;
    return (
        typeof candidate.numero === "string" &&
        typeof candidate.fechaEmisionUTC === "string" &&
        typeof candidate.tipoDoc === "string" &&
        typeof candidate.codigo === "string" &&
        typeof candidate.razon === "string"
    );
};

const normalizeStoredReference = (
    value: unknown,
    fallbackSource: ReferenceSource = "manual_external",
): StoredReference | null => {
    if (!isInfoRefShape(value)) return null;
    const candidate = value as Partial<StoredReference>;
    return {
        id: typeof candidate.id === "string" && candidate.id ? candidate.id : generateLocalId(),
        source: candidate.source ?? fallbackSource,
        linkedDocumentId: candidate.linkedDocumentId,
        linkedDocumentKind: candidate.linkedDocumentKind,
        numero: value.numero,
        fechaEmisionUTC: value.fechaEmisionUTC,
        tipoDoc: value.tipoDoc,
        codigo: value.codigo,
        razon: value.razon,
    };
};

export const parseStoredReferences = (
    raw: string | null | undefined,
): StoredReference[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((item) => normalizeStoredReference(item))
            .filter((item): item is StoredReference => item !== null);
    } catch {
        return [];
    }
};

export const serializeStoredReferences = (refs: StoredReference[]): string =>
    JSON.stringify(refs);

export const toInfoRefElement = (ref: StoredReference): InfoRefElement => ({
    numero: ref.numero,
    fechaEmisionUTC: ref.fechaEmisionUTC,
    tipoDoc: ref.tipoDoc,
    codigo: ref.codigo,
    razon: ref.razon,
});

export const toInfoRefElements = (refs: StoredReference[]): InfoRefElement[] =>
    refs.map(toInfoRefElement);

export const fromInfoRefElement = (
    info: InfoRefElement,
    source: ReferenceSource = "auto",
    extras?: Pick<StoredReference, "linkedDocumentId" | "linkedDocumentKind">,
): StoredReference => ({
    id: generateLocalId(),
    source,
    linkedDocumentId: extras?.linkedDocumentId,
    linkedDocumentKind: extras?.linkedDocumentKind,
    ...info,
});

const referenceDedupeKey = (ref: StoredReference): string =>
    `${ref.tipoDoc}|${ref.numero}|${ref.codigo}`;

export const mergeReferences = (
    base: StoredReference[],
    incoming: StoredReference[],
): StoredReference[] => {
    const seen = new Set<string>();
    const merged: StoredReference[] = [];
    [...base, ...incoming].forEach((ref) => {
        const key = referenceDedupeKey(ref);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(ref);
    });
    return merged;
};

export const ensureLocalIds = (refs: StoredReference[]): StoredReference[] =>
    refs.map((ref) => (ref.id ? ref : { ...ref, id: generateLocalId() }));

export const generateReferenceId = generateLocalId;

export type AutoReferenceFromInvoiceContext =
    | "anular"
    | "notaDeCredito"
    | "devolucion"
    | "notaDeDebito";

const CONTEXT_TO_REFERENCE_CODE: Record<AutoReferenceFromInvoiceContext, keyof typeof REFERENCES_CODES> = {
    anular: "anular",
    notaDeCredito: "notaDeCredito",
    devolucion: "devolucion",
    notaDeDebito: "notaDeDebito",
};

export const buildAutoReferenceFromInvoice = (
    invoice: Pick<InvoiceDataType, "id" | "invoice_key" | "fecha" | "einvoice_kind">,
    context: AutoReferenceFromInvoiceContext,
    razon: string,
): StoredReference => {
    const codigo = REFERENCES_CODES[CONTEXT_TO_REFERENCE_CODE[context]];
    return {
        id: generateLocalId(),
        source: "auto",
        linkedDocumentId: invoice.id,
        linkedDocumentKind: "Invoice",
        numero: `${invoice.invoice_key || ""}`,
        fechaEmisionUTC: computeRfc3339(invoice, "fecha"),
        tipoDoc: resolveReferencedDocumentType(invoice.einvoice_kind || undefined),
        codigo,
        razon,
    };
};

export const buildAutoReferenceFromCreditnote = (
    creditnote: Pick<CreditnoteDataType, "id" | "document_key" | "created" | "description">,
    codigoReferencia: keyof typeof REFERENCES_CODES,
    razon?: string,
): StoredReference => ({
    id: generateLocalId(),
    source: "auto",
    linkedDocumentId: creditnote.id,
    linkedDocumentKind: "Creditnote",
    numero: `${creditnote.document_key || ""}`,
    fechaEmisionUTC: computeRfc3339(creditnote, "created"),
    tipoDoc: REFERENCES_INFORMATION.Creditnote,
    codigo: REFERENCES_CODES[codigoReferencia],
    razon: razon ?? `${creditnote.description || ""}`,
});

export const buildAutoReferenceFromDebitnote = (
    debitnote: Pick<DebitnoteDataType, "id" | "document_key" | "created" | "description">,
    codigoReferencia: keyof typeof REFERENCES_CODES,
    razon?: string,
): StoredReference => ({
    id: generateLocalId(),
    source: "auto",
    linkedDocumentId: debitnote.id,
    linkedDocumentKind: "Debitnote",
    numero: `${debitnote.document_key || ""}`,
    fechaEmisionUTC: computeRfc3339(debitnote, "created"),
    tipoDoc: REFERENCES_INFORMATION.Debitnote,
    codigo: REFERENCES_CODES[codigoReferencia],
    razon: razon ?? `${debitnote.description || ""}`,
});

export const buildBuyInvoiceProviderReference = (
    invoice: Pick<
        BuyInvoiceDataType,
        "ref_doctype" | "ref_numero" | "ref_date" | "ref_razon" | "ref_codigo"
    >,
): StoredReference | null => {
    if (!invoice.ref_doctype || !invoice.ref_numero) return null;
    return {
        id: generateLocalId(),
        source: "auto",
        numero: `${invoice.ref_numero}`,
        fechaEmisionUTC: computeRfc3339(invoice, "ref_date"),
        tipoDoc: `${invoice.ref_doctype}`,
        codigo: `${invoice.ref_codigo || REFERENCES_CODES.referencia}`,
        razon: `${invoice.ref_razon || ""}`,
    };
};

// Detecta si un documento fue rechazado por Hacienda leyendo edocument_status_payload.
export const isDocumentRejectedByHacienda = (
    payload?: string | null,
): boolean => {
    const parsed = parseEDocumentStatusPayload(payload);
    return parsed?.status === "rechazado";
};

// Re-exporta el helper de Constants para mantener un único punto de acceso desde el editor.
export { resolveDefaultTipoDocIR } from "./Constants";
export type { ReferencedDocumentClass } from "./Constants";

// Devuelve el TipoDocIR por defecto a sugerir cuando se selecciona un documento del sistema.
export const resolveDefaultTipoDocIRFromDocument = (params: {
    documentClass: ReferencedDocumentClass;
    einvoiceKind?: string | null;
    edocumentStatusPayload?: string | null;
}): string =>
    resolveDefaultTipoDocIR({
        documentClass: params.documentClass,
        einvoiceKind: params.einvoiceKind,
        isRejectedByHacienda: isDocumentRejectedByHacienda(params.edocumentStatusPayload),
    });

// Indica si el documento fue aceptado por Hacienda. Reexport para conveniencia.
export const isReferencedDocumentAccepted = isEDocumentAtvAccepted;
